import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatMoney, formatDate } from "@/lib/format";
import {
  TrendingUp, TrendingDown, Wallet, Clock, ArrowUpRight, ArrowDownRight,
  Activity, RefreshCcw,
} from "lucide-react";
import {
  AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";

const StatCard = ({ icon: Icon, label, value, trend, trendLabel, tone = "indigo", testid }) => {
  const tones = {
    indigo: "from-indigo-500/20 to-indigo-500/5 text-indigo-400",
    emerald: "from-emerald-500/20 to-emerald-500/5 text-emerald-400",
    red: "from-red-500/20 to-red-500/5 text-red-400",
    amber: "from-amber-500/20 to-amber-500/5 text-amber-400",
  };
  return (
    <div data-testid={testid} className="card-elev p-6 hover:-translate-y-1 transition-all duration-200">
      <div className="flex items-start justify-between">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${tones[tone]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend != null && (
          <div className={`text-xs flex items-center gap-1 ${trend >= 0 ? "text-emerald-400" : "text-red-400"}`}>
            {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {trendLabel}
          </div>
        )}
      </div>
      <div className="mt-5">
        <div className="text-xs text-[#94A3B8] uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-bold mt-1 mono" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value}</div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <div className="text-[#94A3B8] py-20 text-center" data-testid="dashboard-loading">Yükleniyor…</div>;
  }

  const chart = (data.chart_30d || []).map((d) => ({
    ...d,
    label: d.date.slice(5),
  }));

  return (
    <div className="space-y-8 slide-up" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-indigo-400 mb-2">Finansal Panel</div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Günaydın, bugünkü nakit akışınız
          </h1>
          <p className="text-[#94A3B8] mt-1 text-sm">
            Son 30 gün verileri · Döviz kurları: USD {data.rates.USD?.toFixed(2)} · EUR {data.rates.EUR?.toFixed(2)}
          </p>
        </div>
        <button
          onClick={load}
          data-testid="dashboard-refresh"
          className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white px-3 py-2 rounded-lg border border-[#1E293B] hover:border-indigo-500/50 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> Yenile
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          testid="stat-total-balance"
          icon={Wallet}
          label="Toplam Bakiye (TRY)"
          value={formatMoney(data.total_balance_try, "TRY")}
          tone="indigo"
        />
        <StatCard
          testid="stat-monthly-income"
          icon={TrendingUp}
          label="Bu Ay Gelir"
          value={formatMoney(data.monthly_income_try, "TRY")}
          tone="emerald"
        />
        <StatCard
          testid="stat-monthly-expense"
          icon={TrendingDown}
          label="Bu Ay Gider"
          value={formatMoney(data.monthly_expense_try, "TRY")}
          tone="red"
        />
        <StatCard
          testid="stat-forecast"
          icon={Activity}
          label="30 Günlük Tahmini Nakit"
          value={formatMoney(data.net_cashflow_forecast_try, "TRY")}
          tone={data.net_cashflow_forecast_try >= 0 ? "emerald" : "amber"}
        />
      </div>

      {/* Chart + Side */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 card-elev p-6" data-testid="cashflow-chart">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-xs text-[#94A3B8] uppercase tracking-wider">Son 30 Gün</div>
              <div className="font-semibold text-lg mt-0.5">Gelir / Gider Akışı</div>
            </div>
            <div className="flex gap-4 text-xs">
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400"/> Gelir</span>
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-400"/> Gider</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chart}>
              <defs>
                <linearGradient id="gIncome" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.5}/>
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="gExpense" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#EF4444" stopOpacity={0.4}/>
                  <stop offset="100%" stopColor="#EF4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip
                contentStyle={{ background: "#131D31", border: "1px solid #1E293B", borderRadius: 12, color: "#F8FAFC" }}
                formatter={(v) => formatMoney(v, "TRY")}
              />
              <Area type="monotone" dataKey="income" stroke="#10B981" fill="url(#gIncome)" strokeWidth={2} />
              <Area type="monotone" dataKey="expense" stroke="#EF4444" fill="url(#gExpense)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-4 space-y-5">
          {/* Currency breakdown */}
          <div className="card-elev p-6" data-testid="currency-breakdown">
            <div className="text-xs text-[#94A3B8] uppercase tracking-wider mb-4">Para Birimi Dağılımı</div>
            <div className="space-y-3">
              {Object.entries(data.balances_by_currency).map(([cur, bal]) => (
                <div key={cur} className="flex items-center justify-between py-2 border-b border-[#1E293B] last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-xs font-bold">{cur}</div>
                    <span className="text-sm text-[#94A3B8]">{cur === "TRY" ? "Türk Lirası" : cur === "USD" ? "Dolar" : "Euro"}</span>
                  </div>
                  <div className="mono text-sm font-semibold">{formatMoney(bal, cur)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card-elev p-6" data-testid="upcoming-summary">
            <div className="text-xs text-[#94A3B8] uppercase tracking-wider mb-4">30 Gün İçinde Beklenen</div>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-emerald-400 flex items-center gap-2"><Clock className="w-3 h-3"/>Tahsilatlar</span>
                  <span className="mono font-semibold text-sm">{formatMoney(data.upcoming_30d.collections, "TRY")}</span>
                </div>
                <div className="h-1.5 bg-[#1E293B] rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (data.upcoming_30d.collections / Math.max(1, data.upcoming_30d.collections + data.upcoming_30d.payments)) * 100)}%` }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-red-400 flex items-center gap-2"><Clock className="w-3 h-3"/>Ödemeler</span>
                  <span className="mono font-semibold text-sm">{formatMoney(data.upcoming_30d.payments, "TRY")}</span>
                </div>
                <div className="h-1.5 bg-[#1E293B] rounded-full overflow-hidden">
                  <div className="h-full bg-red-500" style={{ width: `${Math.min(100, (data.upcoming_30d.payments / Math.max(1, data.upcoming_30d.collections + data.upcoming_30d.payments)) * 100)}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent + Top categories */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 card-elev p-6" data-testid="recent-transactions">
          <div className="flex items-center justify-between mb-5">
            <div className="font-semibold text-lg">Son İşlemler</div>
            <span className="text-xs text-[#94A3B8]">{data.recent_transactions.length} kayıt</span>
          </div>
          {data.recent_transactions.length === 0 ? (
            <div className="text-sm text-[#94A3B8] py-8 text-center">Henüz işlem yok.</div>
          ) : (
            <div className="space-y-2">
              {data.recent_transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-3 border-b border-[#1E293B] last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${t.type === "income" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                      {t.type === "income" ? <ArrowUpRight className="w-4 h-4"/> : <ArrowDownRight className="w-4 h-4"/>}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{t.description || (t.type === "income" ? "Gelir" : "Gider")}</div>
                      <div className="text-xs text-[#94A3B8]">{formatDate(t.date)}</div>
                    </div>
                  </div>
                  <div className={`mono font-semibold text-sm ${t.type === "income" ? "text-emerald-400" : "text-red-400"}`}>
                    {t.type === "income" ? "+" : "−"}{formatMoney(t.amount, t.currency)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-4 card-elev p-6" data-testid="top-categories">
          <div className="font-semibold text-lg mb-4">En Çok Gider Kategorileri</div>
          {data.top_categories.length === 0 ? (
            <div className="text-sm text-[#94A3B8] py-8 text-center">Veri yok.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={data.top_categories} dataKey="total_try" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={3}>
                    {data.top_categories.map((c, i) => <Cell key={i} fill={c.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#131D31", border: "1px solid #1E293B", borderRadius: 12 }} formatter={(v) => formatMoney(v, "TRY")} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-4">
                {data.top_categories.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                      <span className="text-[#94A3B8]">{c.name}</span>
                    </div>
                    <span className="mono font-medium">{formatMoney(c.total_try, "TRY")}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
